import React from 'react';
import Button from '../../components/ui/Button';
import Mascot from '../../components/ui/Mascot';
import styles from './Placeholder.module.scss';

const PAGE_CONTENT = {
    terms: {
        kicker: '平台政策',
        title: '使用条款',
        intro: 'CodeQuest 致力于为孩子和家庭提供安全、友好、可持续的编程学习环境。下面的条款帮助我们明确平台使用边界、作品规范与双方责任。',
        sections: [
            {
                heading: '1. 适用范围',
                body: '本平台适用于以 Scratch 为基础的创作、闯关和学习活动。使用者应在合理年龄范围内参与学习，遵守平台规则，不得使用本服务从事违法、骚扰或损害他人权益的行为。'
            },
            {
                heading: '2. 账号与学习记录',
                body: [
                    '用户需正确保管账号与密码，因密码泄露、设备遗失或未经授权使用造成的后果，由账号持有人承担。',
                    '平台将保存学习进度、作品状态与关卡记录，供继续学习和个性化推荐之用。',
                    '如果出现异常登录、批量恶意操作或违反规则的情况，平台有权临时限制账号功能。'
                ]
            },
            {
                heading: '3. 作品与内容规范',
                body: [
                    '用户创作的作品应尊重他人、遵循公开分享规则，不得发布侮辱、威胁、歧视、暴力、色情或其他不适合儿童的内容。',
                    '用户保留对其原创作品的知识产权，但同意在平台规则允许的范围内分享和展示其作品。',
                    '如他人发现作品侵权或包含不适宜内容，可向平台提交反馈，平台将在合理范围内处理。'
                ]
            },
            {
                heading: '4. 服务说明',
                body: '平台会持续更新教学内容、关卡设计和交互方式，但不保证所有功能在任何场景下都完全无故障。我们会尽力维持服务稳定性，并在发现问题时尽快修复与优化。'
            },
            {
                heading: '5. 变更和终止',
                body: '平台可能根据教学需求、法律要求或安全风险调整功能与条款。用户继续使用服务即视为接受更新后的条款；若违反规则，平台可限制访问或终止相关权限。'
            }
        ]
    },
    privacy: {
        kicker: '隐私与数据',
        title: '隐私保护',
        intro: '我们重视每位学习者的隐私，尤其关注儿童和家庭用户的数据使用方式。CodeQuest 只在必要范围内收集和处理信息，以支持学习体验、安全与内容优化。',
        sections: [
            {
                heading: '1. 我们收集的信息',
                body: [
                    '账号信息：昵称、密码哈希、登录状态和账户等级等。',
                    '学习数据：完成的关卡、任务进度、作品保存记录和学习表现概览。',
                    '设备与日志：浏览器类型、访问时间、页面状态等，用于稳定性分析与异常排查。'
                ]
            },
            {
                heading: '2. 信息的用途',
                body: [
                    '为用户提供个性化学习路径、关卡推荐和成就记录。',
                    '保障账号安全、识别异常登录和维护平台稳定。',
                    '改进课程内容、教学体验与平台性能。'
                ]
            },
            {
                heading: '3. 共享与保存',
                body: [
                    '仅在必要的服务运营、技术支持和安全合规场景中处理数据。',
                    '我们不会将孩子的学习数据出售给第三方营销机构。',
                    '平台会根据业务需要保留数据，并在不再需要时采取删除或匿名化处理。'
                ]
            },
            {
                heading: '4. 儿童与家庭隐私',
                body: '我们对儿童及其家庭采取更严格的隐私保护原则，重点保障未成年人数据的最小化使用和合理安全控制，并鼓励家长/监护人参与了解和监督。'
            },
            {
                heading: '5. 权利与沟通',
                body: '如果你希望了解、更新或删除你的学习记录，或对数据处理方式有疑问，可以通过帮助支持页面提供反馈。我们将尽力在合理时间内回应。'
            }
        ]
    },
    safety: {
        kicker: '安全与合规',
        title: '安全指南',
        intro: '安全始终是 CodeQuest 设计与运营的核心原则。这里的指南帮助学生、家长和教师在平台内保持安全、尊重和适宜的学习环境。',
        sections: [
            {
                heading: '1. 账号安全',
                body: [
                    '请不要与他人共享密码，避免在公共设备上长期保持登录状态。',
                    '如发现账号异常访问、作品被篡改或可疑登录，请立即退出并联系支持。',
                    '平台会定期审查登录异常和高风险行为，以保护学习环境安全。'
                ]
            },
            {
                heading: '2. 内容安全',
                body: [
                    '作品、评论和分享内容不得出现侮辱、威胁、暴力、色情、诱导欺诈或其他不适合学习场景的内容。',
                    '平台会对公开内容做必要审查，并保留对不合规内容采取隐藏、限制或删除的权利。',
                    '如果发现不适宜内容，请及时提交举报，帮助社区保持温和和安全。'
                ]
            },
            {
                heading: '3. 设备与网络',
                body: '请在稳定的网络环境中访问平台，避免在不可信设备上输入账号信息；同时注意下载、分享和外链风险，尤其是在未受控制的浏览器环境中。'
            },
            {
                heading: '4. 家长与教师参与',
                body: '家长和教师可以关注学习进度、作品分享状态与平台使用情况，帮助孩子建立健康的数字学习习惯。平台的内容与活动均以教育安全和适龄性为前提。'
            },
            {
                heading: '5. 反馈与处置',
                body: '如果你遇到冒犯内容、账号问题、安全威胁或滥用行为，请通过帮助支持页面提交反馈。我们会在合理范围内进行调查和处理。'
            }
        ]
    },
    support: {
        kicker: '服务支持',
        title: '帮助支持',
        intro: '无论你是卡住了关卡、在作品分享时遇到问题，还是需要了解学习路径，CodeQuest 都希望为你提供清晰、友好的帮助。',
        sections: [
            {
                heading: '常见问题',
                body: [
                    '我怎么继续上次的学习进度？登录后，学程记录会自动同步到任务地图和个人主页。',
                    '作品保存失败怎么办？请先在稳定网络下重新保存，必要时检查本地文件是否完整后再重新上传。',
                    '为什么我看不到某个课程或斗关？可能是任务尚未解锁，建议按路线上完成前置关卡。'
                ]
            },
            {
                heading: '建议反馈',
                body: [
                    '如果课程说明不清楚、按钮功能异常、作品上传失败或你想提出改进建议，都可以使用页面中的反馈入口或联系平台支持。',
                    '反馈时尽量提供关卡名称、截图、浏览器信息和操作步骤，方便我们更快定位问题。'
                ]
            },
            {
                heading: '支持方式',
                body: '平台会在团队工作时间内优先处理账号问题、作品问题、课程内容问题和安全举报。你也可以在学习中通过页面提示和邮件联系入口获取进一步帮助。'
            }
        ]
    }
};

export default function Placeholder ({title, hint, page}) {
    const content = PAGE_CONTENT[page] || PAGE_CONTENT[title?.toLowerCase()] || {
        kicker: '帮助中心',
        title: title || '页面',
        intro: hint || '这个页面正在建设中，很快就能和你见面啦！',
        sections: [
            {
                heading: '说明',
                body: hint || '这个页面正在建设中，很快就能和你见面啦！'
            }
        ]
    };

    return (
        <div className={`cq-container ${styles.wrap}`}>
            <header className={styles.header}>
                <Mascot size={120} />
                <div className={styles.headerText}>
                    <span className={styles.kicker}>{content.kicker}</span>
                    <h1 className={styles.title}>{content.title}</h1>
                    <p className={styles.lead}>{content.intro}</p>
                </div>
            </header>

            <div className={styles.content}>
                {content.sections.map((section, idx) => (
                    <section key={`${content.title}-${idx}`} className={styles.card}>
                        <h2>{section.heading}</h2>
                        {Array.isArray(section.body) ? (
                            <ul className={styles.list}>
                                {section.body.map(item => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        ) : (
                            <p>{section.body}</p>
                        )}
                    </section>
                ))}
            </div>

            <div className={styles.actions}>
                <Button to="/" variant="soft">返回首页</Button>
            </div>
        </div>
    );
}
